import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, FileSpreadsheet, CheckCircle, AlertTriangle, Loader2, MessageSquare, ThumbsUp, ThumbsDown } from 'lucide-react';
import { useAuth } from '../../contexts/TestAuthContext';
import { api, uploadFiles } from '../../utils/api';
import TaxReturnUpload from './TaxReturnUpload';
import UseLastClaim from './UseLastClaim';

/**
 * Step3_AI_Assistant - AI-Powered Data Entry
 * 
 * Features:
 * - Drag/drop PDF upload
 * - AI extraction with confidence scores
 * - Category suggestions
 * - Anomaly detection
 * - Chat interface for questions
 * - Tax Return Parser (Phase 2)
 * - Predictive Data Entry (Phase 2 Day 4-6)
 */

interface Step3AIProps {
  onDataExtracted?: (data: any) => void;
  existingData?: any;
  onSave?: () => Promise<void>;  // ✅ NEW: Trigger save before AI processing
  initialFiles?: File[];  // Files from SmartUploader for seamless handoff
}

export default function Step3_AI_Assistant({ onDataExtracted, existingData, onSave, initialFiles }: Step3AIProps = {}) {
  const [uploading, setUploading] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState(existingData?.uploadedDocuments || []);
  const [hasProcessedInitialFiles, setHasProcessedInitialFiles] = useState(false);
  
  // Check if data came from bulk upload (higher confidence)
  const fromBulkUpload = existingData?.fromBulkUpload || false;
  const bulkUploadConfidence = fromBulkUpload ? 0.95 : 0.85;
  
  // CRITICAL FIX: Initialize extractedData from existing financial_data
  const [extractedData, setExtractedData] = useState(() => {
    console.log('🔵 Step3_AI_Assistant: Initializing extractedData from existingData');
    console.log('🔵 existingData.financial_data keys:', existingData?.financial_data ? Object.keys(existingData.financial_data) : 'NONE');
    
    if (existingData?.financial_data) {
      const revenue = [];
      const expenses = {};
      const payroll = [];
      const confidence = fromBulkUpload ? 0.95 : 0.85;
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      // PRIORITY 1: Check for data_YYYY format (from CSV upload)
      const hasYearlyData = Object.keys(existingData.financial_data).some(key => key.startsWith('data_'));
      
      if (hasYearlyData) {
        console.log('✅ Found data_YYYY format - processing yearly data');
        Object.keys(existingData.financial_data).forEach(yearKey => {
          if (yearKey.startsWith('data_')) {
            const year = parseInt(yearKey.replace('data_', ''));
            const yearData = existingData.financial_data[yearKey];
            
            if (Array.isArray(yearData)) {
              yearData.forEach((monthData: any) => {
                if (monthData && monthData.month) {
                  // Add revenue
                  revenue.push({
                    year,
                    month: monthNames[monthData.month - 1] || 'N/A',
                    amount: monthData.revenue || 0,
                    confidence: confidence
                  });
                  
                  // Aggregate expenses by category
                  if (monthData.expenses) {
                    Object.keys(monthData.expenses).forEach(category => {
                      if (!expenses[category]) expenses[category] = {};
                      expenses[category][year] = (expenses[category][year] || 0) + (monthData.expenses[category] || 0);
                    });
                  }
                  
                  // Add payroll
                  if (monthData.payroll) {
                    payroll.push({
                      year,
                      month: monthNames[monthData.month - 1] || 'N/A',
                      amount: monthData.payroll.total || 0,
                      confidence: confidence
                    });
                  }
                }
              });
            }
          }
        });
        
        console.log(`✅ Processed ${revenue.length} revenue entries, ${payroll.length} payroll entries`);
        return {
          revenue,
          expenses,
          payroll,
          confidence_score: confidence,
          fromBulkUpload: fromBulkUpload
        };
      }
      
      console.log('⚠️ No data_YYYY format found - trying array format');
      // PRIORITY 2: Check for array format (legacy)
      if (existingData.financial_data.revenue && Array.isArray(existingData.financial_data.revenue)) {
        console.log('✅ Found revenue array format');
        existingData.financial_data.revenue.forEach((item: any) => {
          revenue.push({
            year: item.year,
            month: typeof item.month === 'number' ? monthNames[item.month - 1] : item.month,
            amount: item.amount || 0,
            confidence: confidence
          });
        });
      }
      
      if (existingData.financial_data.payroll && Array.isArray(existingData.financial_data.payroll)) {
        existingData.financial_data.payroll.forEach((item: any) => {
          payroll.push({
            year: item.year,
            month: typeof item.month === 'number' ? monthNames[item.month - 1] : item.month,
            amount: item.amount || 0,
            confidence: confidence
          });
        });
      }
      
      return {
        revenue,
        expenses,
        payroll,
        confidence_score: confidence,
        fromBulkUpload: fromBulkUpload
      };
    }
    
    console.log('⚠️ No financial_data found in existingData');
    return null;
  });
  
  const [anomalies, setAnomalies] = useState([]);
  const [showChat, setShowChat] = useState(false);
  const [chatQuestion, setChatQuestion] = useState('');
  const [chatResponse, setChatResponse] = useState('');
  const [loadingChat, setLoadingChat] = useState(false);
  const [mergedData, setMergedData] = useState(null);
  
  // NEW: Messiness detection state
  const [showMessinessModal, setShowMessinessModal] = useState(false);
  const [messinessResult, setMessinessResult] = useState<{
    needs_cleanup: boolean;
    confidence: number;
    reasons: string[];
    recommendation: string;
    details: any;
    file?: File;
  } | null>(null);
  const [checkingMessiness, setCheckingMessiness] = useState(false);
  
  // NEW: Track multiple P&L uploads by year
  const [plFiles, setPlFiles] = useState(existingData?.plFiles || []);
  
  // NEW: Track multiple 1065 uploads by year - Initialize with existing files
  const [tax1065Files, setTax1065Files] = useState(existingData?.tax1065Files || []);
  
  // NEW: Track approved items for Review Progress
  const [approvedItems, setApprovedItems] = useState(new Set());
  const [editingCell, setEditingCell] = React.useState<string | null>(null);
  const [editValue, setEditValue] = React.useState<string>('');

  // Get authentication and backend URL
  const { getToken } = useAuth();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  // IMMEDIATE SAVE: Save files to database as soon as they're uploaded
  const saveToDatabase = React.useCallback(async (filesData: any) => {
    try {
      const reportId = existingData?.id || new URLSearchParams(window.location.search).get('resume');
      if (!reportId) {
        console.log('⚠️ No report ID, skipping immediate save');
        return;
      }

      const token = await getToken();
      
      // Use XMLHttpRequest instead of fetch() to avoid Clerk rrweb-recorder conflict
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', `${BACKEND_URL}/api/v1/reports/${reportId}/update`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            console.log('✅ Files saved immediately to database');
            console.log('   plFiles:', filesData.plFiles?.length || 0);
            console.log('   uploadedDocuments:', filesData.uploadedDocuments?.length || 0);
            console.log('   tax1065Files:', filesData.tax1065Files?.length || 0);
            resolve(xhr.responseText);
          } else {
            console.error('❌ Save failed:', xhr.status, xhr.responseText);
            reject(new Error(`Save failed: ${xhr.status}`));
          }
        };
        
        xhr.onerror = () => {
          console.error('❌ Network error during save');
          reject(new Error('Network error'));
        };
        
        xhr.send(JSON.stringify({
          step: 3,
          data: filesData,
          last_modified: new Date().toISOString()
        }));
      });
    } catch (error) {
      console.error('❌ Save error:', error);
    }
  }, [BACKEND_URL, existingData, getToken]);
  
  // IMMEDIATE SAVE: When files change, save them automatically
  React.useEffect(() => {
    if (tax1065Files.length > 0 || plFiles.length > 0 || uploadedDocuments.length > 0) {
      const dataToSave = {
        tax1065Files,
        plFiles,
        uploadedDocuments
      };
      console.log('💾 Auto-saving uploaded files...', dataToSave);
      saveToDatabase(dataToSave);
    }
  }, [tax1065Files, plFiles, uploadedDocuments, saveToDatabase]);
  
  // NEW: Auto-merge when P&L files change - BUT skip if data came from bulk upload
  React.useEffect(() => {
    // Skip merging if data already came from bulk upload (it's already processed)
    if (extractedData?.fromBulkUpload) {
      console.log('⏭️ Skipping P&L merge - data already from bulk upload');
      return;
    }
    if (plFiles.length > 0) {
      console.log(`Merging ${plFiles.length} P&L files...`);
      mergeExtractedData(plFiles);
    }
  }, [plFiles]);
  
  // NEW: Auto-merge when P&L files change OR when component mounts with existing documents
  React.useEffect(() => {
    // Skip merging if data already came from bulk upload (it's already processed)
    if (extractedData?.fromBulkUpload) {
      console.log('⏭️ Skipping document merge - data already from bulk upload');
      return;
    }
    if (uploadedDocuments.length > 0) {
      console.log('🔄 Re-merging uploaded documents:', uploadedDocuments.length);
      mergeExtractedData(uploadedDocuments);
    }
  }, [uploadedDocuments.length]); // Re-merge when documents change or on mount
  
  // NEW: Pass extracted data to parent when it changes
  React.useEffect(() => {
    if (onDataExtracted) {
      const dataToPass = {
        ...extractedData,
        tax1065Files: tax1065Files,
        plFiles: plFiles,  // ✅ P&L files
        uploadedDocuments: uploadedDocuments  // ✅ CRITICAL FIX: Include bank statements & other docs!
      };
      console.log('🔵 Step3_AI_Assistant: Passing extracted data to parent');
      console.log('🔵 Extracted Data Structure:', JSON.stringify(dataToPass, null, 2));
      console.log('🔵 Payroll Data:', extractedData?.payroll);
      console.log('🔵 Tax 1065 Files:', tax1065Files);
      console.log('🔵 P&L Files:', plFiles);
      console.log('🔵 Uploaded Documents (Bank Statements, etc):', uploadedDocuments);
      onDataExtracted(dataToPass);
    }
  }, [extractedData, tax1065Files, plFiles, uploadedDocuments]); // Added uploadedDocuments to dependencies

  // NEW: Detect year from filename or content (wrapped in useCallback to prevent re-renders)
  const detectYear = useCallback((filename, extractedData = null) => {
    // Try filename first: "P&L_2022.xlsx", "1065_2019.pdf", "ProfitLoss_Jan_2021.csv"
    const yearMatch = filename.match(/20\d{2}/); // Matches 2000-2099
    if (yearMatch) {
      return parseInt(yearMatch[0]);
    }
    
    // Try extracted data (tax_year from 1065, or year from P&L)
    if (extractedData?.tax_year) {
      return parseInt(extractedData.tax_year);
    }
    
    if (extractedData?.time_period) {
      const yearMatch = extractedData.time_period.match(/20\d{2}/);
      if (yearMatch) {
        return parseInt(yearMatch[0]);
      }
    }
    
    // Default to current year if can't detect
    return new Date().getFullYear();
  }, []);
  
  // NEW: Detect month from filename (for monthly P&Ls) (wrapped in useCallback to prevent re-renders)
  const detectMonth = useCallback((filename) => {
    const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    const lowerFilename = filename.toLowerCase();
    
    for (let i = 0; i < months.length; i++) {
      if (lowerFilename.includes(months[i])) {
        return i + 1; // Return 1-12
      }
    }
    
    return null; // Annual file, not monthly
  }, []);

  // NEW: Check if document needs BI Cleanup (messiness detection)
  const checkMessiness = useCallback(async (file: File): Promise<{
    needs_cleanup: boolean;
    confidence: number;
    reasons: string[];
    recommendation: string;
    details: any;
  }> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Use centralized API utility for file upload
      const result = await uploadFiles('/api/v1/cleanup/check-messiness', formData, { showErrorToast: false });
      return result;
    } catch (error) {
      console.error('Messiness check error:', error);
      // On error, default to direct processing (don't block user)
      return {
        needs_cleanup: false,
        confidence: 0.5,
        reasons: ['Unable to analyze - proceeding with direct processing'],
        recommendation: 'direct',
        details: { error: String(error) }
      };
    }
  }, []);

  // NEW: Handle redirect to BI Cleanup
  const handleRedirectToCleanup = useCallback(() => {
    if (!messinessResult?.file) return;
    
    // Store wizard context for return
    const wizardContext = {
      from_wizard: true,
      return_to: '/wizard-v2',
      step: 3,
      report_id: existingData?.id || new URLSearchParams(window.location.search).get('resume'),
      pending_file: {
        name: messinessResult.file.name,
        size: messinessResult.file.size,
        type: messinessResult.file.type
      }
    };
    
    sessionStorage.setItem('wizard_context', JSON.stringify(wizardContext));
    
    // Store the file for cleanup upload
    // Note: We can't store File objects directly, so we'll need to re-upload
    sessionStorage.setItem('cleanup_pending_filename', messinessResult.file.name);
    
    // Navigate to cleanup
    window.location.href = '/cleanup?from_wizard=true';
  }, [messinessResult, existingData]);

  // NEW: Handle continue with direct processing (skip cleanup)
  const handleContinueDirectly = useCallback(() => {
    setShowMessinessModal(false);
    setMessinessResult(null);
    // Continue with normal processing - the file is already in pending state
  }, []);

  // Drag & drop handler - supports multiple files
  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    setUploading(true);
    
    try {
      // ✅ STEP 1: SAVE CURRENT STATE BEFORE EXPENSIVE AI PROCESSING
      console.log('💾 STEP 1: Saving current wizard state before AI processing (prevents data loss)...');
      if (onSave) {
        try {
          await onSave();
          console.log('✅ Current state saved! Now safe to process with AI.');
        } catch (saveError) {
          console.error('⚠️ Save before processing failed:', saveError);
          const proceed = window.confirm('Warning: Failed to save current state. AI processing costs money. Continue anyway?');
          if (!proceed) {
            setUploading(false);
            return;
          }
        }
      }
      
      // ✅ STEP 2: PROCESS FILES WITH AI (COSTS MONEY!)
      console.log('🤖 STEP 2: Processing files with AI (this costs money)...');
      const uploadPromises = acceptedFiles.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);

        // Use centralized API utility for file upload
        const result = await uploadFiles('/api/v1/ai/extract-pdf', formData, { showErrorToast: true });
        return {
          filename: file.name,
          size: file.size,
          uploadedAt: new Date().toISOString(),
          data: result.data,
          audit_id: result.audit_id
        };
      });

      const results = await Promise.all(uploadPromises);
      
      // Add to uploaded documents list
      setUploadedDocuments(prev => [...prev, ...results]);

      // Merge all extracted data
      mergeExtractedData([...uploadedDocuments, ...results]);

    } catch (error) {
      console.error('Upload error:', error);
      setUploadError('Failed to upload one or more files. Please try again with PDF, Excel, CSV, or QuickBooks format.');
    } finally {
      setUploading(false);
    }
  }, [uploadedDocuments, onSave]);

  // APPROACH A: Seamless Handoff - auto-process files from SmartUploader
  React.useEffect(() => {
    if (initialFiles && initialFiles.length > 0 && !hasProcessedInitialFiles && !uploading) {
      console.log('📁 Step3_AI_Assistant: Auto-processing files from SmartUploader:', initialFiles.map(f => f.name));
      setHasProcessedInitialFiles(true);
      // Trigger the onDrop handler with the initial files
      onDrop(initialFiles);
    }
  }, [initialFiles, hasProcessedInitialFiles, uploading, onDrop]);

  // General document upload dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 
      'application/pdf': ['.pdf'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
      'application/x-csv': ['.csv'],
      'text/plain': ['.iif'], // QuickBooks IIF
      'application/vnd.intu.qbo': ['.qbo'] // QuickBooks Online
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    multiple: true  // Enable multiple file upload
  });

  // P&L-specific upload handler (uses XMLHttpRequest to avoid rrweb-recorder conflict)
  const onDropPL = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    setUploading(true);
    
    try {
      console.log('📁 PHASE 1: Checking files and uploading...');
      
      // ✅ NEW: Check PDFs for messiness before processing
      const pdfFiles = acceptedFiles.filter(f => f.name.toLowerCase().endsWith('.pdf'));
      
      if (pdfFiles.length > 0) {
        console.log(`🔍 Checking ${pdfFiles.length} PDF file(s) for messiness...`);
        setCheckingMessiness(true);
        
        // Check the first PDF (or could check all)
        const firstPdf = pdfFiles[0];
        const result = await checkMessiness(firstPdf);
        
        setCheckingMessiness(false);
        console.log('📊 Messiness check result:', result);
        
        // If needs cleanup, show modal to user
        if (result.needs_cleanup && result.recommendation === 'cleanup') {
          setMessinessResult({ ...result, file: firstPdf });
          setShowMessinessModal(true);
          setUploading(false);
          
          // Store files for later (if user chooses to continue directly)
          setPendingFilesForProcessing(prev => [...prev, ...acceptedFiles]);
          return; // Wait for user decision
        }
      }
      
      // Continue with normal upload flow
      console.log('📁 Document appears clean - proceeding with direct processing...');
      
      // ✅ PHASE 1: Just save file metadata first (no AI processing)
      const fileMetadata = acceptedFiles.map(file => {
        const yearMatch = file.name.match(/20\d{2}/); // Extract year from filename
        return {
          filename: file.name,
          type: file.name.endsWith('.pdf') ? 'pdf' : 
                file.name.endsWith('.xlsx') || file.name.endsWith('.xls') ? 'excel' : 'csv',
          year: yearMatch ? parseInt(yearMatch[0]) : new Date().getFullYear(),
          size: file.size,
          uploadedAt: new Date().toISOString(),
          processed: false,  // Not yet processed by AI
          rawFile: file  // Keep reference for later processing
        };
      });
      
      // Add to plFiles immediately (unprocessed)
      setPlFiles(prev => {
        const updated = [...prev, ...fileMetadata];
        console.log('✅ PHASE 1 COMPLETE: Files uploaded (not processed yet):', updated.length);
        return updated;
      });
      
      // Update parent immediately so files get saved
      if (onDataExtracted) {
        onDataExtracted({
          plFiles: [...plFiles, ...fileMetadata],
          uploadedDocuments: [...uploadedDocuments, ...fileMetadata],
          tax1065Files: tax1065Files
        });
      }
      
      setUploadSuccess(`${acceptedFiles.length} file(s) uploaded successfully! Click "Process Files with AI" to extract financial data.`);
      setUploading(false);
      
      // Store files for later processing
      setPendingFilesForProcessing(prev => [...prev, ...acceptedFiles]);
      
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError('Failed to upload files. Please try again.');
      setUploading(false);
    }
  }, [plFiles, uploadedDocuments, tax1065Files, onDataExtracted]);
  
  // ✅ NEW: Separate AI processing function
  const [pendingFilesForProcessing, setPendingFilesForProcessing] = useState<File[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  // Inline validation state (replaces alert() calls)
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [clearMessage, setClearMessage] = useState<string | null>(null);
  const [clearError, setClearError] = useState<string | null>(null);

  const processFilesWithAI = useCallback(async () => {
    if (pendingFilesForProcessing.length === 0) {
      setUploadError('No files to process. Please upload files first.');
      return;
    }
    
    setIsProcessing(true);
    
    try {
      // Get auth token
      const token = await getToken();
      
      // ✅ PHASE 1: Save current state BEFORE expensive AI processing
      console.log('💾 PHASE 1: Saving current state before AI processing...');
      if (onSave) {
        try {
          await onSave();
          console.log('✅ State saved successfully. Now processing files with AI...');
        } catch (saveError) {
          console.error('⚠️ Save failed, but continuing with AI processing:', saveError);
        }
      }
      
      // ✅ PHASE 2: Process files with AI (this costs money!)
      console.log('🤖 PHASE 2: Processing files with AI (this costs money)...');
      const uploadPromises = pendingFilesForProcessing.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);

        // Use XMLHttpRequest instead of fetch to avoid rrweb-recorder conflict
        const result = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          
          xhr.open('POST', `${BACKEND_URL}/api/v1/financial-docs/parse-profit-loss`);
          
          // Add authorization header if token exists
          if (token) {
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          }
          
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                const data = JSON.parse(xhr.responseText);
                resolve(data);
              } catch (e) {
                reject(new Error('Invalid JSON response'));
              }
            } else {
              try {
                const errorData = JSON.parse(xhr.responseText);
                reject(new Error(errorData.detail || `Failed to parse ${file.name}`));
              } catch (e) {
                reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
              }
            }
          };
          
          xhr.onerror = () => reject(new Error('Network error'));
          xhr.ontimeout = () => reject(new Error('Request timeout'));
          
          xhr.send(formData);
        });
        
        // Auto-fill form with extracted P&L data
        if (result.success && result.extracted_data) {
          console.log(`✅ P&L data extracted successfully from ${file.name}!`);
          console.log('Full result:', JSON.stringify(result, null, 2));
          console.log('Extracted data structure:', result.extracted_data);
          
          const plData = result.extracted_data;
          
          // =========================================================================
          // MULTI-YEAR HANDLING: Check if this is a QuickBooks multi-year file
          // =========================================================================
          if (plData.multi_year_data && plData.years_found && plData.years_found.length > 0) {
            console.log(`📊 Multi-year P&L detected! Years: ${plData.years_found.join(', ')}`);
            
            // Return array of entries, one per year
            const multiYearEntries = plData.years_found.map((year: number) => {
              const yearData = plData.multi_year_data[String(year)];
              if (!yearData) return null;
              
              const mappedData: any = {
                revenue: yearData.revenue || {},
                expenses: yearData.expenses?.categories || {},
                payroll: yearData.payroll || {},
                metadata: {
                  source: file.name,
                  fileType: result.file_type,
                  extractedAt: new Date().toISOString(),
                  multi_year: true,
                  original_years: plData.years_found
                }
              };
              
              console.log(`   ✅ Year ${year}: Revenue=$${yearData.revenue?.total?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
              
              return {
                filename: `${file.name.replace('.xlsx', '')}_${year}.xlsx`,
                type: result.file_type,
                data: mappedData,
                year: year,
                month: null,
                size: file.size,
                uploadedAt: new Date().toISOString(),
                extractedAt: new Date().toISOString(),
                confidence: result.confidence || 0.85
              };
            }).filter(Boolean);
            
            console.log(`📊 Created ${multiYearEntries.length} P&L entries from multi-year file`);
            return multiYearEntries; // Return array of entries
          }
          
          // =========================================================================
          // SINGLE-YEAR HANDLING: Original logic for single-year files
          // =========================================================================
          const mappedData: any = {
            revenue: {},
            expenses: {},
            metadata: {
              source: file.name,
              fileType: result.file_type,
              extractedAt: new Date().toISOString()
            }
          };

          // Map revenue data
          if (plData.revenue) {
            // Revenue can be by month or aggregate
            if (typeof plData.revenue === 'object') {
              mappedData.revenue = plData.revenue;
            } else if (typeof plData.revenue === 'number') {
              mappedData.revenue.total = plData.revenue;
            }
          }

          // Map expenses data
          if (plData.expenses) {
            mappedData.expenses = plData.expenses;
          }

          // Map payroll if present
          if (plData.payroll) {
            mappedData.payroll = plData.payroll;
          }

          // Map profit_loss section if present
          if (plData.profit_loss) {
            Object.assign(mappedData, plData.profit_loss);
          }

          // NEW: Detect year and month from filename or content
          const detectedYear = detectYear(file.name, result.extracted_data);
          const detectedMonth = detectMonth(file.name);
          
          const plFileEntry = {
            filename: file.name,
            type: result.file_type,
            data: mappedData,
            year: detectedYear,
            month: detectedMonth,
            size: file.size,  // ✅ CRITICAL FIX: Include file size for display
            uploadedAt: new Date().toISOString(),  // ✅ Renamed from extractedAt for consistency
            extractedAt: new Date().toISOString(),  // Keep both for backward compatibility
            confidence: result.confidence || 0.85
          };
          
          console.log(`✅ P&L extracted: ${file.name} → Year ${detectedYear}${detectedMonth ? `, Month ${detectedMonth}` : ''}`);
          
          return plFileEntry;
        }
        
        return null;
      });

      // Wait for all uploads to complete
      const results = await Promise.all(uploadPromises);
      
      // Flatten results - some may be arrays (multi-year files), others single entries
      const flattenedResults: any[] = [];
      results.forEach(r => {
        if (r === null) return;
        if (Array.isArray(r)) {
          // Multi-year file returned array of entries
          flattenedResults.push(...r);
        } else {
          // Single-year file returned single entry
          flattenedResults.push(r);
        }
      });
      
      const successfulUploads = flattenedResults.filter(r => r !== null);
      
      if (successfulUploads.length > 0) {
        // Add all to P&L files list
        setPlFiles(prev => {
          const updated = [...prev, ...successfulUploads];
          console.log('🔵 plFiles state updated:', updated.length, 'total files');
          console.log('🔵 plFiles content:', JSON.stringify(updated, null, 2).substring(0, 500));
          return updated;
        });
        
        // Also add to general uploaded documents
        setUploadedDocuments(prev => {
          const updated = [...prev, ...successfulUploads];
          console.log('🔵 uploadedDocuments state updated:', updated.length, 'total files');
          return updated;
        });

        // Extract years for display
        const years = [...new Set(successfulUploads.map(f => f.year))].sort();
        
        // Show more informative message for multi-year files
        const isMultiYear = successfulUploads.some(f => f.data?.metadata?.multi_year);
        const alertMessage = isMultiYear 
          ? `✅ Multi-year P&L extracted successfully!\n\n${successfulUploads.length} year(s) of data processed.\n\nYears detected: ${years.join(', ')}`
          : `✅ P&L files extracted successfully!\n\n${successfulUploads.length} file(s) processed.\n\nYears detected: ${years.join(', ')}`;
        
        setUploadSuccess(alertMessage);
        
        console.log(`✅ All P&L files processed. Years: ${years.join(', ')}`);
      } else {
        throw new Error('No files were successfully processed');
      }
    } catch (error) {
      console.error('P&L upload error:', error);
      setUploadError(`Failed to parse P&L: ${error.message}. Please try a different file format or contact support.`);
    } finally {
      setIsProcessing(false);
      setPendingFilesForProcessing([]);  // Clear pending files after processing
    }
  }, [BACKEND_URL, detectYear, detectMonth, getToken, onSave, pendingFilesForProcessing]);

  // P&L-specific dropzone - NOW SUPPORTS MULTIPLE FILES
  const plDropzone = useDropzone({
    onDrop: onDropPL,
    accept: { 
      'application/pdf': ['.pdf'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
      'application/x-csv': ['.csv'],
      'text/plain': ['.iif'],
      'application/vnd.intu.qbo': ['.qbo']
    },
    maxSize: 10 * 1024 * 1024,
    multiple: true  // ✅ ENABLE MULTIPLE P&L FILES (4 years of data)
  });

  // Merge data from multiple documents
  const mergeExtractedData = (documents) => {
    // CRITICAL FIX: Start with existing extractedData to preserve previously loaded data
    const merged = {
      revenue: extractedData?.revenue ? [...extractedData.revenue] : [],
      expenses: extractedData?.expenses ? {...extractedData.expenses} : {},
      payroll: extractedData?.payroll ? [...extractedData.payroll] : [],
      confidence_score: extractedData?.confidence_score || 0,
      document_count: documents.length
    };

    documents.forEach(doc => {
      // ✅ CRITICAL FIX: Check if doc.data exists first (old/corrupt data might not have it)
      if (!doc || !doc.data) {
        console.warn('⚠️ Skipping document with missing data:', doc);
        return;
      }
      
      // Merge revenue - handle both array and object formats
      if (doc.data.revenue) {
        if (Array.isArray(doc.data.revenue)) {
          // Already an array
          merged.revenue.push(...doc.data.revenue);
        } else if (typeof doc.data.revenue === 'object') {
          // Object format from P&L extraction: { total: X, breakdown: {...}, monthly: {...} }
          // PRIORITY: Use monthly data first (preferred for BI), fallback to breakdown
          if (doc.data.revenue.monthly) {
            // Monthly data available - use this (12 months for BI)
            Object.entries(doc.data.revenue.monthly).forEach(([month, amount]) => {
              merged.revenue.push({
                month: month,
                amount: amount,
                confidence: 0.85,
                year: doc.year || new Date().getFullYear()
              });
            });
          } else if (doc.data.revenue.breakdown) {
            // Fallback: Convert breakdown to array format (only if no monthly data)
            Object.entries(doc.data.revenue.breakdown).forEach(([category, amount]) => {
              merged.revenue.push({
                month: category,
                amount: amount,
                confidence: 0.85,
                year: doc.year || new Date().getFullYear()
              });
            });
          }
        }
      }
      
      // Merge expenses - keep year breakdown for multi-year view
      if (doc.data.expenses) {
        const year = doc.year || new Date().getFullYear();
        Object.entries(doc.data.expenses).forEach(([category, expenseData]) => {
          // Handle both number and object formats
          const amount = typeof expenseData === 'number' 
            ? expenseData 
            : (expenseData?.total || expenseData?.amount || 0);
          
          // Store expenses by category and year
          if (!merged.expenses[category]) {
            merged.expenses[category] = {};
          }
          merged.expenses[category][year] = amount;
        });
      }
      
      // Merge payroll - handle both array and object formats
      if (doc.data.payroll) {
        if (Array.isArray(doc.data.payroll)) {
          merged.payroll.push(...doc.data.payroll);
        } else if (typeof doc.data.payroll === 'object') {
          // Object format: { total: X, monthly: {...} }
          if (doc.data.payroll.monthly) {
            Object.entries(doc.data.payroll.monthly).forEach(([month, amount]) => {
              merged.payroll.push({
                month: month,
                amount: amount,
                year: doc.year || new Date().getFullYear()
              });
            });
          }
        }
      }
    });

    // Calculate average confidence (with null safety)
    const totalConfidence = documents.reduce((sum, doc) => {
      const confidence = doc.confidence || (doc.data && doc.data.confidence_score) || 0;
      return sum + confidence;
    }, 0);
    merged.confidence_score = documents.length > 0 ? totalConfidence / documents.length : 0;

    console.log('📦 Merged extracted data:', merged);
    console.log('📦 Revenue items:', merged.revenue.length);
    console.log('📦 Expense categories:', Object.keys(merged.expenses).length);
    console.log('📦 Payroll items:', merged.payroll.length);
    console.log('📦 Payroll details:', JSON.stringify(merged.payroll, null, 2));

    setMergedData(merged);
    setExtractedData(merged);
    detectAnomalies(merged);
  };

  // Remove a document
  const removeDocument = (index) => {
    const updated = uploadedDocuments.filter((_, i) => i !== index);
    setUploadedDocuments(updated);
    if (updated.length > 0) {
      mergeExtractedData(updated);
    } else {
      setExtractedData(null);
      setMergedData(null);
    }
  };

  // Detect anomalies
  const detectAnomalies = async (data) => {
    try {
      const result = await api.post('/api/v1/ai/detect-anomalies', { financial_data: data }, { showErrorToast: false });
      setAnomalies(result.anomalies || []);
    } catch (error) {
      console.error('Anomaly detection error:', error);
    }
  };

  // Ask AI chat (optionally pass question directly to avoid state update delay)
  const handleAskAI = async (directQuestion?: string) => {
    const questionToUse = directQuestion || chatQuestion;
    if (!questionToUse || typeof questionToUse !== 'string' || !questionToUse.trim()) return;

    setLoadingChat(true);
    try {
      // Get authentication token
      const token = await getToken();
      
      // Prepare enriched context for AI with multi-year data
      const enrichedContext = {
        ...extractedData,
        // Include multi-year file details
        uploaded_files: plFiles.map(f => ({
          filename: f.filename,
          year: f.year,
          month: f.month,
          confidence: f.confidence
        })),
        // Include year coverage
        years_covered: [...new Set(plFiles.map(f => f.year))].sort(),
        // Include document count
        total_documents: uploadedDocuments.length,
        // Include P&L specific data
        pl_file_count: plFiles.length,
        // Add metadata for better analysis
        analysis_context: {
          multi_year: plFiles.length > 1,
          years: [...new Set(plFiles.map(f => f.year))].sort(),
          has_monthly_data: true
        }
      };
      
      // CRITICAL FIX: Use XMLHttpRequest instead of fetch() to avoid Clerk rrweb-recorder error
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BACKEND_URL}/api/v1/ai/explain`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              resolve(data);
            } catch (e) {
              reject(new Error('Failed to parse AI response'));
            }
          } else {
            reject(new Error(`AI explanation failed: ${xhr.statusText}`));
          }
        };
        
        xhr.onerror = () => reject(new Error('Network error during AI request'));
        xhr.send(JSON.stringify({
          claim_id: 'demo',
          question: questionToUse,
          calculation_results: enrichedContext
        }));
      });
      setChatResponse(result.explanation || result.answer || 'No response from AI.');
    } catch (error) {
      console.error('Chat error:', error);
      setChatResponse(`Unable to generate response: ${error.message}. Please try again.`);
    } finally {
      setLoadingChat(false);
    }
  };

  // Confidence badge component
  const ConfidenceBadge = ({ score, source = null }) => {
    const getColor = (score) => {
      if (score >= 0.9) return 'bg-green-100 text-green-800';
      if (score >= 0.7) return 'bg-yellow-100 text-yellow-800';
      return 'bg-red-100 text-red-800';
    };

    const getLabel = (score, source) => {
      if (source === 'bulk_upload') return 'Bulk Upload';
      if (score >= 0.9) return 'High';
      if (score >= 0.7) return 'Medium';
      return 'Low';
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-semibold ${getColor(score)}`}>
        {getLabel(score, source)} ({(score * 100).toFixed(0)}%)
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Inline upload error/success messages */}
      {uploadError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start justify-between">
          <p className="text-red-700 text-sm">{uploadError}</p>
          <button onClick={() => setUploadError(null)} className="text-red-400 hover:text-red-600 ml-2 text-sm font-bold">&times;</button>
        </div>
      )}
      {uploadSuccess && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start justify-between">
          <p className="text-green-700 text-sm">{uploadSuccess}</p>
          <button onClick={() => setUploadSuccess(null)} className="text-green-400 hover:text-green-600 ml-2 text-sm font-bold">&times;</button>
        </div>
      )}
      {clearMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start justify-between">
          <p className="text-green-700 text-sm">{clearMessage}</p>
          <button onClick={() => setClearMessage(null)} className="text-green-400 hover:text-green-600 ml-2 text-sm font-bold">&times;</button>
        </div>
      )}
      {clearError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start justify-between">
          <p className="text-red-700 text-sm">{clearError}</p>
          <button onClick={() => setClearError(null)} className="text-red-400 hover:text-red-600 ml-2 text-sm font-bold">&times;</button>
        </div>
      )}

      {/* Messiness Detection Modal */}
      {showMessinessModal && messinessResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg mx-4 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-4">
              <div className="flex items-center gap-3">
                <span className="text-3xl">🔍</span>
                <div>
                  <h2 className="text-xl font-bold">Document Analysis Complete</h2>
                  <p className="text-orange-100 text-sm">We've analyzed your uploaded file</p>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6">
              {/* File Info */}
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-xl">📄</span>
                  <span className="font-medium text-gray-900">{messinessResult.file?.name}</span>
                </div>
              </div>
              
              {/* Recommendation */}
              <div className={`rounded-lg p-4 mb-4 ${
                messinessResult.recommendation === 'cleanup' 
                  ? 'bg-orange-50 border border-orange-200' 
                  : 'bg-green-50 border border-green-200'
              }`}>
                <div className="flex items-start gap-3">
                  <span className="text-2xl">
                    {messinessResult.recommendation === 'cleanup' ? '⚠️' : '✅'}
                  </span>
                  <div>
                    <h3 className={`font-semibold ${
                      messinessResult.recommendation === 'cleanup' 
                        ? 'text-orange-800' 
                        : 'text-green-800'
                    }`}>
                      {messinessResult.recommendation === 'cleanup' 
                        ? 'This document needs cleanup' 
                        : 'Document looks good!'}
                    </h3>
                    <p className={`text-sm mt-1 ${
                      messinessResult.recommendation === 'cleanup' 
                        ? 'text-orange-700' 
                        : 'text-green-700'
                    }`}>
                      {messinessResult.recommendation === 'cleanup'
                        ? 'We detected issues that may affect data accuracy. BI Cleanup will extract and validate your data with human review.'
                        : 'This document can be processed directly without cleanup.'}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Reasons */}
              {messinessResult.reasons.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Analysis Details:</h4>
                  <ul className="space-y-1">
                    {messinessResult.reasons.map((reason, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                        <span className="text-orange-500">•</span>
                        {reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Confidence Score */}
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
                <span>Confidence:</span>
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${
                      messinessResult.confidence > 0.7 ? 'bg-green-500' :
                      messinessResult.confidence > 0.5 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${messinessResult.confidence * 100}%` }}
                  />
                </div>
                <span>{Math.round(messinessResult.confidence * 100)}%</span>
              </div>
              
              {/* Actions */}
              <div className="space-y-3">
                {messinessResult.recommendation === 'cleanup' && (
                  <button
                    onClick={handleRedirectToCleanup}
                    className="w-full px-4 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-red-600 transition-all flex items-center justify-center gap-2"
                  >
                    🧹 Use BI Cleanup (Recommended)
                  </button>
                )}
                
                <button
                  onClick={handleContinueDirectly}
                  className={`w-full px-4 py-3 font-semibold rounded-lg transition-colors ${
                    messinessResult.recommendation === 'cleanup'
                      ? 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {messinessResult.recommendation === 'cleanup' 
                    ? 'Continue Anyway (May have errors)' 
                    : 'Continue with Processing'}
                </button>
                
                <button
                  onClick={() => {
                    setShowMessinessModal(false);
                    setMessinessResult(null);
                    setUploading(false);
                  }}
                  className="w-full px-4 py-2 text-gray-500 hover:text-gray-700 text-sm"
                >
                  Cancel and choose different file
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Checking Messiness Indicator */}
      {checkingMessiness && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-40">
          <div className="bg-white rounded-lg shadow-xl p-6 flex items-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            <div>
              <p className="font-medium text-gray-900">Analyzing document...</p>
              <p className="text-sm text-gray-500">Checking if cleanup is needed</p>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">Step 3: AI-Assisted Data Entry</h1>

        {/* Predictive Data Entry - Use Last Claim */}
        <UseLastClaim 
          onApplyPredictions={(predictions) => {
            // Apply predictions to form data
            console.log('Applying predictions:', predictions);
            if (predictions.predictions) {
              const pred = predictions.predictions;
              
              // Build merged data structure
              const mergedFromPredictions = {
                revenue: [],
                expenses: {},
                payroll: [],
                confidence_score: predictions.confidence || 0.85,
                source: 'predictive_entry'
              };
              
              // Set revenue if available
              if (pred.revenue?.data && Array.isArray(pred.revenue.data)) {
                mergedFromPredictions.revenue = pred.revenue.data;
              }
              
              // Set expenses if available
              if (pred.expenses?.data) {
                mergedFromPredictions.expenses = pred.expenses.data;
              }
              
              // Set payroll if available
              if (pred.payroll?.data && Array.isArray(pred.payroll.data)) {
                mergedFromPredictions.payroll = pred.payroll.data;
              }
              
              // Apply to extractedData state (this auto-fills the form)
              setExtractedData(mergedFromPredictions);
              setMergedData(mergedFromPredictions);
              
              console.log('✅ Predictions applied to form:', mergedFromPredictions);
            }
          }}
          userId="demo-user-123"
          demoMode={true}
        />

        {/* Upload Area - Always visible for adding more documents */}
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors mb-6 ${
            isDragActive
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-blue-400'
          }`}
        >
          <input {...getInputProps()} />
          {uploading ? (
            <div className="flex flex-col items-center">
              <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-3" />
              <p className="text-lg font-semibold">AI is analyzing your documents...</p>
              <p className="text-sm text-gray-600 mt-1">This may take 10-30 seconds per document</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <Upload className="w-12 h-12 text-gray-400 mb-3" />
              <p className="text-lg font-semibold mb-1">
                {isDragActive ? 'Drop PDFs here...' : uploadedDocuments.length > 0 ? 'Add More Documents' : 'Drag & drop your financial documents'}
              </p>
              <p className="text-sm text-gray-600">
                {uploadedDocuments.length > 0 ? 'Upload additional P&L, bank statements, tax returns' : 'or click to browse (max 10MB per file)'}
              </p>
              <p className="text-xs text-gray-500 mt-2">✨ You can upload multiple documents</p>
            </div>
          )}
        </div>

        {/* Uploaded Documents List */}
        {uploadedDocuments.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">📁 Uploaded Documents ({uploadedDocuments.length})</h2>
              <span className="text-sm text-gray-600">All data merged automatically</span>
            </div>
            <div className="space-y-2">
              {uploadedDocuments.map((doc, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center flex-1">
                    <FileText className="w-5 h-5 text-blue-600 mr-3" />
                    <div>
                      <p className="font-semibold">{doc.filename}</p>
                      <p className="text-xs text-gray-500">
                        {doc.size ? `${(doc.size / 1024).toFixed(1)} KB` : 'Size unknown'} • Uploaded {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleTimeString() : doc.extractedAt ? new Date(doc.extractedAt).toLocaleTimeString() : 'Recently'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <ConfidenceBadge score={doc.data.confidence_score || 0.85} />
                    <button
                      onClick={() => removeDocument(index)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* P&L Upload Section - Dedicated */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileSpreadsheet className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Upload Profit & Loss Statement</h2>
              <p className="text-sm text-gray-600">
                Upload your P&L in any format - PDF, Excel, CSV, or QuickBooks
              </p>
            </div>
          </div>

          <div {...plDropzone.getRootProps()} className={`
            border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition
            ${plDropzone.isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'}
          `}>
            <input {...plDropzone.getInputProps()} />
            <div className="flex flex-col items-center">
              <Upload className="w-12 h-12 text-gray-400 mb-3" />
              <p className="font-semibold text-gray-700 mb-1">
                {plDropzone.isDragActive ? 'Drop your P&L here...' : 'Drag & drop your P&L statement'}
              </p>
              <p className="text-sm text-gray-500 mb-4">
                PDF, Excel (.xlsx, .xls), CSV, QuickBooks (.iif, .qbo)
              </p>
              <p className="text-xs text-gray-400">
                AI will extract revenue, expenses, and payroll automatically
              </p>
            </div>
          </div>
          
          {/* NEW: Display uploaded P&L files by year */}
          {plFiles.length > 0 && (
            <div className="mt-6 border-t pt-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900">📁 Uploaded P&L Files ({plFiles.length})</h3>
                {plFiles.length > 0 && (
                  <button
                    onClick={async () => {
                      if (window.confirm('Are you sure you want to clear all P&L data? This cannot be undone.')) {
                        // Clear frontend state
                        setPlFiles([]);
                        setExtractedData(null);
                        setMergedData(null);
                        setUploadedDocuments([]);
                        
                        // Clear parent data
                        if (onDataExtracted) {
                          onDataExtracted({
                            plFiles: [],
                            tax1065Files: existingData?.tax1065Files || [],
                            financial_data: {}
                          });
                        }
                        
                        // CRITICAL: Save the cleared state to database using XMLHttpRequest (avoids Clerk issues)
                        try {
                          const reportId = existingData?.id || new URLSearchParams(window.location.search).get('resume');
                          if (reportId) {
                            const token = await getToken();
                            
                            await new Promise((resolve, reject) => {
                              const xhr = new XMLHttpRequest();
                              xhr.open('PUT', `${BACKEND_URL}/api/v1/reports/${reportId}/update`);
                              xhr.setRequestHeader('Content-Type', 'application/json');
                              xhr.setRequestHeader('Authorization', `Bearer ${token}`);
                              
                              xhr.onload = () => {
                                if (xhr.status >= 200 && xhr.status < 300) {
                                  resolve(xhr.responseText);
                                  setClearMessage('All P&L data cleared from database.');
                                } else {
                                  reject(new Error('Database update failed'));
                                  setClearError('Data cleared from screen but database update failed. Please refresh and try again.');
                                }
                              };

                              xhr.onerror = () => {
                                reject(new Error('Network error'));
                                setClearError('Network error. Data cleared from screen but not saved to database.');
                              };

                              xhr.send(JSON.stringify({
                                step: 3,
                                data: {
                                  plFiles: [],
                                  financial_data: {},
                                  tax1065Files: existingData?.tax1065Files || []
                                },
                                last_modified: new Date().toISOString()
                              }));
                            });
                          } else {
                            setClearMessage('All P&L data cleared.');
                          }
                        } catch (error) {
                          console.error('Error clearing data:', error);
                        }
                      }
                    }}
                    className="text-xs text-gray-500 hover:text-gray-700 underline"
                  >
                    Clear all
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 gap-3">
                {plFiles
                  .sort((a, b) => a.year - b.year)  // Sort by year
                  .map((plFile, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileSpreadsheet className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="font-medium text-gray-900">{plFile.filename}</p>
                          <p className="text-xs text-gray-600">
                            Year: <span className="font-semibold">{plFile.year}</span>
                            {plFile.month && ` • Month: ${plFile.month}`}
                            {plFile.confidence && ` • Confidence: ${(plFile.confidence * 100).toFixed(0)}%`}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          // Remove from plFiles array
                          const updatedPlFiles = plFiles.filter((_, i) => i !== index);
                          setPlFiles(updatedPlFiles);
                          
                          // If no P&L files left, clear all extracted data
                          if (updatedPlFiles.length === 0) {
                            setExtractedData(null);
                            setMergedData(null);
                            setUploadedDocuments([]);
                            // Also clear parent data
                            props.onDataExtracted({
                              plFiles: [],
                              tax1065Files: props.data?.tax1065Files || []
                            });
                          }
                        }}
                        className="text-red-600 hover:text-red-800 text-sm font-medium px-3 py-1 rounded hover:bg-red-100"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
              </div>
              
              {/* PROCESS FILES WITH AI BUTTON - Shows when files are pending */}
              {pendingFilesForProcessing.length > 0 && (
                <div className="mt-4 p-4 bg-yellow-50 border-2 border-yellow-400 rounded-lg animate-pulse">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-bold text-yellow-800">
                        🤖 {pendingFilesForProcessing.length} file(s) ready for AI processing
                      </p>
                      <p className="text-xs text-yellow-700 mt-1">
                        Click the button to extract financial data from your P&L files
                      </p>
                    </div>
                    <button
                      onClick={processFilesWithAI}
                      disabled={isProcessing}
                      className={`px-6 py-3 rounded-lg font-bold text-white shadow-lg transition-all ${
                        isProcessing 
                          ? 'bg-gray-400 cursor-not-allowed' 
                          : 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 hover:shadow-xl'
                      }`}
                    >
                      {isProcessing ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                          </svg>
                          Processing...
                        </span>
                      ) : (
                        '🚀 Process Files with AI'
                      )}
                    </button>
                  </div>
                </div>
              )}
              
              {/* Year coverage indicator */}
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Years covered:</strong> {[...new Set(plFiles.map(f => f.year))].sort().join(', ')}
                </p>
                {plFiles.some(f => f.month) && (
                  <p className="text-xs text-blue-700 mt-1">
                    Monthly data detected for detailed analysis
                  </p>
                )}
                
                {/* Missing year warning */}
                {plFiles.length < 4 && (
                  <div className="mt-3 p-2 bg-orange-50 border border-orange-300 rounded">
                    <p className="text-xs text-orange-800 font-semibold">
                      ⚠️ Warning: BI analysis requires 4 consecutive years of data
                    </p>
                    <p className="text-xs text-orange-700 mt-1">
                      You have {plFiles.length} year(s). Please upload {4 - plFiles.length} more P&L file(s).
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* How P&L Upload Works */}
          <div className="mt-4 bg-blue-50 rounded-lg p-4">
            <p className="text-sm font-semibold text-blue-900 mb-2">How it works:</p>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Upload your monthly or annual P&L statement</li>
              <li>• AI extracts revenue, expenses (by category), and payroll</li>
              <li>• Data auto-fills into the form below</li>
              <li>• Review and adjust as needed</li>
              <li>• Boost accuracy and save 15+ minutes</li>
            </ul>
          </div>
        </div>

        {/* Tax Return Parser - Phase 2 Feature */}
        <TaxReturnUpload 
          onDataParsed={(parsedTax1065Files) => {
            console.log('Tax data parsed - updating state:', parsedTax1065Files);
            setTax1065Files(parsedTax1065Files);
          }}
          plData={extractedData}
          demoMode={false}
          existingFiles={tax1065Files}
        />

        {/* Anomaly Alerts - Informational, not blocking */}
        {anomalies.length > 0 && (
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-blue-500 mr-3 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-blue-800">💡 Data Insights</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Our AI has analyzed your financial data and identified the following patterns:
                </p>
                {anomalies.length > 0 && anomalies[0].type ? (
                  <ul className="mt-3 space-y-2">
                    {anomalies.map((anomaly, idx) => (
                      <li key={idx} className="text-sm text-blue-700 bg-white p-2 rounded">
                        <strong className="text-blue-900">{anomaly.type || 'Pattern'}:</strong>{' '}
                        {anomaly.description || anomaly.recommendation || 'Unusual pattern detected'}
                        {anomaly.severity && (
                          <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                            anomaly.severity === 'high' ? 'bg-red-100 text-red-700' :
                            anomaly.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {anomaly.severity}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="mt-3 text-sm text-blue-600 bg-white p-2 rounded">
                    ✓ No significant anomalies detected. Your data appears consistent.
                  </div>
                )}
                <p className="text-xs text-blue-600 mt-3">
                  Note: These insights are informational and won't block your claim submission.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Extracted Data Table */}
        {extractedData && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl font-bold">AI Extracted Data</h2>
                <p className="text-sm text-gray-600">
                  {uploadedDocuments.length > 1 ? `Merged from ${uploadedDocuments.length} documents` : 'From 1 document'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={async () => {
                    if (window.confirm('⚠️ Clear ALL extracted financial data?\n\nThis will remove:\n• All revenue data\n• All expense data\n• All payroll data\n• All uploaded P&L files\n\nYou will need to re-upload your files. Continue?')) {
                      // Clear frontend state
                      setPlFiles([]);
                      setExtractedData(null);
                      setMergedData(null);
                      setUploadedDocuments([]);
                      
                      // Clear parent data
                      if (onDataExtracted) {
                        onDataExtracted({
                          plFiles: [],
                          tax1065Files: existingData?.tax1065Files || [],
                          financial_data: {}
                        });
                      }
                      
                      // CRITICAL: Save the cleared state to database using XMLHttpRequest (avoids Clerk issues)
                      try {
                        const reportId = existingData?.id || new URLSearchParams(window.location.search).get('resume');
                        if (reportId) {
                          const token = await getToken();
                          
                          await new Promise((resolve, reject) => {
                            const xhr = new XMLHttpRequest();
                            xhr.open('PUT', `${BACKEND_URL}/api/v1/reports/${reportId}/update`);
                            xhr.setRequestHeader('Content-Type', 'application/json');
                            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
                            
                            xhr.onload = () => {
                              if (xhr.status >= 200 && xhr.status < 300) {
                                resolve(xhr.responseText);
                                setClearMessage('All financial data cleared from database. You can now upload fresh P&L files.');
                              } else {
                                reject(new Error('Database update failed'));
                                setClearError('Data cleared from screen but database update failed. Please refresh and try again.');
                              }
                            };

                            xhr.onerror = () => {
                              reject(new Error('Network error'));
                              setClearError('Network error. Data cleared from screen but not saved to database.');
                            };

                            xhr.send(JSON.stringify({
                              plFiles: [],
                              financial_data: {},
                              step: 3,
                              last_modified: new Date().toISOString()
                            }));
                          });
                        } else {
                          setClearMessage('All financial data cleared. You can now upload fresh P&L files.');
                        }
                      } catch (error) {
                        console.error('Error clearing data:', error);
                      }
                    }
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1 rounded border border-gray-300 hover:bg-gray-50"
                >
                  Clear all data
                </button>
                <ConfidenceBadge 
                  score={extractedData.confidence_score || 0.85} 
                  source={extractedData.fromBulkUpload ? 'bulk_upload' : null}
                />
              </div>
            </div>

            {/* Revenue Section */}
            {extractedData.revenue && extractedData.revenue.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold text-lg mb-3">💰 Revenue (Multi-Year)</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border p-2 text-left">Year</th>
                        <th className="border p-2 text-left">Month</th>
                        <th className="border p-2 text-right">Amount</th>
                        <th className="border p-2 text-center">Confidence</th>
                        <th className="border p-2 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {extractedData.revenue
                        .sort((a, b) => {
                          // Sort by year first, then by month
                          if (a.year !== b.year) return (a.year || 0) - (b.year || 0);
                          // Month order (Jan=0, Feb=1, etc.)
                          const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
                          const aMonth = months.indexOf(a.month?.toLowerCase().substring(0, 3)) || 0;
                          const bMonth = months.indexOf(b.month?.toLowerCase().substring(0, 3)) || 0;
                          return aMonth - bMonth;
                        })
                        .map((item, idx) => {
                        const itemId = `revenue-${item.year}-${item.month}`;
                        const isApproved = approvedItems.has(itemId);
                        
                        return (
                        <tr key={idx} className={`hover:bg-gray-50 ${isApproved ? 'bg-green-50' : ''}`}>
                          <td className="border p-2 font-semibold text-blue-700">{item.year || 'N/A'}</td>
                          <td className="border p-2">{item.month}</td>
                          <td className="border p-2 text-right font-mono">
                            ${item.amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="border p-2 text-center">
                            <ConfidenceBadge score={item.confidence || 0.85} />
                          </td>
                          <td className="border p-2 text-center">
                            <button 
                              onClick={() => {
                                const newApproved = new Set(approvedItems);
                                if (isApproved) {
                                  newApproved.delete(itemId);
                                } else {
                                  newApproved.add(itemId);
                                }
                                setApprovedItems(newApproved);
                              }}
                              className={`mr-2 ${isApproved ? 'text-green-600' : 'text-gray-400'} hover:text-green-800 transition-colors`}
                              title={isApproved ? 'Approved' : 'Click to approve'}
                            >
                              <CheckCircle className="w-5 h-5 inline" fill={isApproved ? 'currentColor' : 'none'} />
                            </button>
                            <button className="text-gray-600 hover:text-gray-800">
                              Edit
                            </button>
                          </td>
                        </tr>
                      );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Expenses Section - Multi-Year Matrix */}
            {extractedData.expenses && Object.keys(extractedData.expenses).length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-lg">📊 Expenses by Category - Multi-Year View</h3>
                  <button
                    onClick={async () => {
                      if (window.confirm('Clear all extracted financial data? This will remove all revenue, expenses, and payroll data. You will need to re-upload your P&L files.')) {
                        // Clear frontend state
                        setPlFiles([]);
                        setExtractedData(null);
                        setMergedData(null);
                        setUploadedDocuments([]);
                        
                        // Clear parent data
                        if (onDataExtracted) {
                          onDataExtracted({
                            plFiles: [],
                            tax1065Files: existingData?.tax1065Files || [],
                            financial_data: {}
                          });
                        }
                        
                        // CRITICAL: Save the cleared state to database using XMLHttpRequest (avoids Clerk issues)
                        try {
                          const reportId = existingData?.id || new URLSearchParams(window.location.search).get('resume');
                          if (reportId) {
                            const token = await getToken();
                            
                            await new Promise((resolve, reject) => {
                              const xhr = new XMLHttpRequest();
                              xhr.open('PUT', `${BACKEND_URL}/api/v1/reports/${reportId}/update`);
                              xhr.setRequestHeader('Content-Type', 'application/json');
                              xhr.setRequestHeader('Authorization', `Bearer ${token}`);
                              
                              xhr.onload = () => {
                                if (xhr.status >= 200 && xhr.status < 300) {
                                  resolve(xhr.responseText);
                                  setClearMessage('All financial data cleared from database.');
                                } else {
                                  reject(new Error('Database update failed'));
                                  setClearError('Data cleared from screen but database update failed. Please refresh and try again.');
                                }
                              };

                              xhr.onerror = () => {
                                reject(new Error('Network error'));
                                setClearError('Network error. Data cleared from screen but not saved to database.');
                              };

                              xhr.send(JSON.stringify({
                                step: 3,
                                data: {
                                  plFiles: [],
                                  financial_data: {},
                                  tax1065Files: existingData?.tax1065Files || []
                                },
                                last_modified: new Date().toISOString()
                              }));
                            });
                          } else {
                            setClearMessage('All financial data cleared.');
                          }
                        } catch (error) {
                          console.error('Error clearing data:', error);
                        }
                      }
                    }}
                    className="text-xs text-gray-500 hover:text-gray-700 underline"
                  >
                    Clear all
                  </button>
                </div>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left px-4 py-2 font-semibold text-gray-700">Category</th>
                        {(() => {
                          // Get all unique years from expenses
                          const allYears = new Set();
                          Object.values(extractedData.expenses).forEach(yearData => {
                            if (typeof yearData === 'object') {
                              Object.keys(yearData).forEach(year => allYears.add(parseInt(year)));
                            }
                          });
                          const sortedYears = Array.from(allYears).sort();
                          return sortedYears.map(year => (
                            <th key={year} className="text-right px-4 py-2 font-semibold text-gray-700">{year}</th>
                          ));
                        })()}
                        <th className="text-right px-4 py-2 font-semibold text-blue-700 bg-blue-50">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {Object.entries(extractedData.expenses).map(([category, yearData], idx) => {
                        // Get all unique years
                        const allYears = new Set();
                        Object.values(extractedData.expenses).forEach(yd => {
                          if (typeof yd === 'object') {
                            Object.keys(yd).forEach(year => allYears.add(parseInt(year)));
                          }
                        });
                        const sortedYears = Array.from(allYears).sort();
                        
                        // Calculate total across all years for this category
                        const total = typeof yearData === 'object' 
                          ? Object.values(yearData).reduce((sum, amt) => sum + (typeof amt === 'number' ? amt : 0), 0)
                          : (typeof yearData === 'number' ? yearData : 0);
                        
                        return (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-2 font-medium text-gray-900">{category}</td>
                            {sortedYears.map(year => {
                              const amount = typeof yearData === 'object' ? yearData[year] : 0;
                              const cellId = `${category}-${year}`;
                              const isEditing = editingCell === cellId;
                              
                              return (
                                <td 
                                  key={year} 
                                  className="text-right px-4 py-2 font-mono text-gray-700 cursor-pointer hover:bg-blue-50"
                                  onClick={() => {
                                    if (!isEditing) {
                                      setEditingCell(cellId);
                                      setEditValue(amount ? amount.toString() : '0');
                                    }
                                  }}
                                >
                                  {isEditing ? (
                                    <input
                                      type="number"
                                      value={editValue}
                                      onChange={(e) => setEditValue(e.target.value)}
                                      onBlur={() => {
                                        // Save the edited value
                                        const newAmount = parseFloat(editValue) || 0;
                                        setExtractedData((prev: any) => {
                                          if (!prev) return prev;
                                          return {
                                            ...prev,
                                            expenses: {
                                              ...prev.expenses,
                                              [category]: {
                                                ...(typeof prev.expenses[category] === 'object' ? prev.expenses[category] : {}),
                                                [year]: newAmount
                                              }
                                            }
                                          };
                                        });
                                        setEditingCell(null);
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.currentTarget.blur();
                                        } else if (e.key === 'Escape') {
                                          setEditingCell(null);
                                        }
                                      }}
                                      autoFocus
                                      className="w-full px-2 py-1 border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
                                    />
                                  ) : (
                                    <span className="hover:underline">
                                      {amount ? `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                                    </span>
                                  )}
                                </td>
                              );
                            })}
                            <td className="text-right px-4 py-2 font-mono font-semibold text-blue-700 bg-blue-50">
                              ${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                        );
                      })}
                      
                      {/* Total Row */}
                      <tr className="bg-blue-50 font-bold">
                        <td className="px-4 py-2 text-blue-900">Total Expenses</td>
                        {(() => {
                          const allYears = new Set();
                          Object.values(extractedData.expenses).forEach(yearData => {
                            if (typeof yearData === 'object') {
                              Object.keys(yearData).forEach(year => allYears.add(parseInt(year)));
                            }
                          });
                          const sortedYears = Array.from(allYears).sort();
                          
                          return sortedYears.map(year => {
                            const yearTotal = Object.values(extractedData.expenses).reduce((sum, yearData) => {
                              if (typeof yearData === 'object' && yearData[year]) {
                                return sum + yearData[year];
                              }
                              return sum;
                            }, 0);
                            return (
                              <td key={year} className="text-right px-4 py-2 font-mono text-blue-900">
                                ${yearTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                            );
                          });
                        })()}
                        <td className="text-right px-4 py-2 font-mono text-blue-900 bg-blue-100 text-base">
                          ${Object.entries(extractedData.expenses).reduce((sum, [_, yearData]) => {
                            if (typeof yearData === 'object') {
                              return sum + Object.values(yearData).reduce((s, a) => s + (typeof a === 'number' ? a : 0), 0);
                            }
                            return sum + (typeof yearData === 'number' ? yearData : 0);
                          }, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Form 1065 & K-1 Tax Data Section - Only show if data exists AND has non-zero values */}
            {existingData?.tax_data && existingData.tax_data.length > 0 && 
             existingData.tax_data.some((tax: any) => tax.partnership_gross_receipts > 0 || tax.partner_a_guaranteed_payments > 0) && (
              <div className="mb-6">
                <h3 className="font-semibold text-lg mb-3">📋 Form 1065 & K-1 Tax Data (Annual)</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse bg-white rounded-lg shadow">
                    <thead>
                      <tr className="bg-gradient-to-r from-indigo-50 to-blue-50">
                        <th className="border p-2 text-left font-semibold">Tax Year</th>
                        <th className="border p-2 text-right font-semibold">Gross Receipts</th>
                        <th className="border p-2 text-right font-semibold">COGS</th>
                        <th className="border p-2 text-right font-semibold">Wages (Line 9)</th>
                        <th className="border p-2 text-right font-semibold">Net Income</th>
                        <th className="border p-2 text-left font-semibold">Partner A</th>
                        <th className="border p-2 text-left font-semibold">Partner B</th>
                      </tr>
                    </thead>
                    <tbody>
                      {existingData.tax_data.map((tax: any, idx: number) => (
                        <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="border p-2 font-semibold text-indigo-700">{tax.tax_year}</td>
                          <td className="border p-2 text-right font-mono">${(tax.partnership_gross_receipts || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="border p-2 text-right font-mono">${(tax.partnership_cogs || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="border p-2 text-right font-mono">${(tax.partnership_salaries_wages || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="border p-2 text-right font-mono font-semibold text-green-700">
                            ${(tax.partnership_net_ordinary_income || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="border p-2 text-sm">
                            <div className="font-medium">{tax.partner_a_name || 'Partner A'}</div>
                            <div className="text-gray-600">{tax.partner_a_ownership_pct || 50}% ownership</div>
                            <div className="text-indigo-600">Guaranteed: ${(tax.partner_a_guaranteed_payments || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          </td>
                          <td className="border p-2 text-sm">
                            <div className="font-medium">{tax.partner_b_name || 'Partner B'}</div>
                            <div className="text-gray-600">{tax.partner_b_ownership_pct || 50}% ownership</div>
                            <div className="text-indigo-600">Guaranteed: ${(tax.partner_b_guaranteed_payments || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Reconciliation Results Section */}
            {existingData?.reconciliation && existingData.reconciliation.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold text-lg mb-3">🔍 Reconciliation: Monthly P&L vs Form 1065</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse bg-white rounded-lg shadow">
                    <thead>
                      <tr className="bg-gradient-to-r from-blue-50 to-cyan-50">
                        <th className="border p-2 text-left font-semibold">Year</th>
                        <th className="border p-2 text-center font-semibold" colSpan={2}>Revenue</th>
                        <th className="border p-2 text-center font-semibold" colSpan={2}>COGS</th>
                        <th className="border p-2 text-center font-semibold" colSpan={2}>Payroll</th>
                      </tr>
                      <tr className="bg-gray-100 text-xs">
                        <th className="border p-1"></th>
                        <th className="border p-1">Monthly</th>
                        <th className="border p-1">1065</th>
                        <th className="border p-1">Monthly</th>
                        <th className="border p-1">1065</th>
                        <th className="border p-1">Monthly</th>
                        <th className="border p-1">Expected*</th>
                      </tr>
                    </thead>
                    <tbody>
                      {existingData.reconciliation.map((recon: any, idx: number) => (
                        <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="border p-2 font-semibold">{recon.year}</td>
                          {/* Revenue */}
                          <td className="border p-2 text-right font-mono text-sm">
                            ${(recon.revenue?.monthly_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="border p-2 text-right font-mono text-sm">
                            ${(recon.revenue?.tax_return || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            <span className={`ml-1 text-xs ${
                              recon.revenue?.status?.includes('MATCH') ? 'text-green-600' :
                              recon.revenue?.status?.includes('ACCEPTABLE') ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {recon.revenue?.status}
                            </span>
                          </td>
                          {/* COGS */}
                          <td className="border p-2 text-right font-mono text-sm">
                            ${(recon.cogs?.monthly_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="border p-2 text-right font-mono text-sm">
                            ${(recon.cogs?.tax_return || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            <span className={`ml-1 text-xs ${
                              recon.cogs?.status?.includes('MATCH') ? 'text-green-600' :
                              recon.cogs?.status?.includes('ACCEPTABLE') ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {recon.cogs?.status}
                            </span>
                          </td>
                          {/* Payroll */}
                          <td className="border p-2 text-right font-mono text-sm">
                            ${(recon.payroll?.monthly_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="border p-2 text-right font-mono text-sm">
                            ${(recon.payroll?.expected_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            <span className={`ml-1 text-xs ${
                              recon.payroll?.status?.includes('MATCH') ? 'text-green-600' :
                              recon.payroll?.status?.includes('ACCEPTABLE') ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {recon.payroll?.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-xs text-gray-500 mt-2">
                    * Expected Payroll = Form 1065 Line 9 (W-2 wages) + Partner Guaranteed Payments (Schedule K Line 4c)
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4 mt-6">
              <button
                onClick={() => {
                  // Auto-trigger forensic analysis (no typing required)
                  const forensicPrompt = 'Analyze this multi-year P&L data: (1) Revenue trends and year-over-year % changes, (2) Expense ratio shifts (COGS%, Payroll%, Rent%), (3) Any anomalies or unusual patterns (>30% variance), (4) BI impact on Step 1/Step 2 calculations, (5) Red flags or data quality issues. Provide 5 specific bullet points with numbers and percentages.';
                  setShowChat(true);
                  // Run AI with the forensic prompt directly (bypass state update delay)
                  handleAskAI(forensicPrompt);
                  // Clear the question after running so user can ask new questions
                  setTimeout(() => {
                    setChatQuestion('');
                  }, 500);
                }}
                disabled={loadingChat}
                className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition flex items-center justify-center disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <MessageSquare className="w-5 h-5 mr-2" />
                {loadingChat ? 'Running Forensic Analysis...' : '🔍 AI Forensic Analysis (1-Click)'}
              </button>
              <button 
                onClick={() => setShowChat(true)}
                className="px-6 py-3 border-2 border-blue-600 text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition"
              >
                💬 Ask Custom Question
              </button>
            </div>
          </div>
        )}

        {/* AI Chat Sidebar */}
        {showChat && (
          <div className="fixed right-0 top-0 h-full w-96 bg-white shadow-2xl border-l border-gray-200 p-6 overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">💬 Ask AI</h3>
              <button
                onClick={() => setShowChat(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {/* FORENSIC ANALYSIS - ONE CLICK */}
            <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-blue-50 rounded-lg border-2 border-blue-200">
              <p className="text-sm font-bold text-blue-900 mb-2">🔍 Forensic Analysis (Recommended)</p>
              <button
                onClick={() => {
                  const forensicPrompt = 'Run comprehensive forensic analysis: (1) Revenue trends & YoY % changes across all years, (2) Expense ratios (COGS%, Payroll%, Rent%) and shifts >10%, (3) Month-by-month anomalies >30% from average, (4) BI Step 1/Step 2 impact assessment, (5) Data quality red flags. Output: 5 specific bullet points with exact numbers and percentages.';
                  setChatQuestion(forensicPrompt);
                  // Pass question directly to avoid state update delay
                  handleAskAI(forensicPrompt);
                }}
                disabled={loadingChat}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 transition text-sm"
              >
                {loadingChat ? '⏳ Analyzing...' : '▶️ Run Full Analysis (No Typing)'}
              </button>
            </div>

            {/* Pre-filled questions */}
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">Or ask specific questions:</p>
              <div className="space-y-2">
                <button
                  onClick={() => setChatQuestion('What are the top 3 expense categories and how have they changed year-over-year?')}
                  className="w-full text-left text-sm bg-gray-100 hover:bg-gray-200 p-2 rounded"
                >
                  📊 Top expense trends
                </button>
                <button
                  onClick={() => setChatQuestion('Identify any months with revenue or expense anomalies (>30% variance from average).')}
                  className="w-full text-left text-sm bg-gray-100 hover:bg-gray-200 p-2 rounded"
                >
                  ⚠️ Find anomalies
                </button>
                <button
                  onClick={() => setChatQuestion('How will this data affect my BI Step 1 and Step 2 calculations?')}
                  className="w-full text-left text-sm bg-gray-100 hover:bg-gray-200 p-2 rounded"
                >
                  🎯 BI impact
                </button>
              </div>
            </div>

            {/* Chat input */}
            <div className="mb-4">
              <div className="relative">
                <textarea
                  value={chatQuestion}
                  onChange={(e) => setChatQuestion(e.target.value)}
                  placeholder="Ask anything about your financial data..."
                  className="w-full border rounded p-3 h-24 resize-none pr-20"
                  onKeyPress={(e) => {
                    // Send on Enter (without Shift)
                    if (e.key === 'Enter' && !e.shiftKey && chatQuestion.trim() && !loadingChat) {
                      e.preventDefault();
                      handleAskAI();
                    }
                  }}
                />
                {chatQuestion && (
                  <button
                    onClick={() => setChatQuestion('')}
                    className="absolute right-2 top-2 text-gray-400 hover:text-gray-600 text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200"
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="flex gap-2 mt-2">
                <button
                  onClick={handleAskAI}
                  disabled={loadingChat || !chatQuestion.trim()}
                  className="flex-1 bg-blue-600 text-white py-2 rounded font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
                >
                  {loadingChat ? (
                    <span className="flex items-center justify-center">
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      AI is thinking...
                    </span>
                  ) : (
                    'Send Question'
                  )}
                </button>
                {chatResponse && (
                  <button
                    onClick={() => {
                      setChatResponse('');
                      setChatQuestion('');
                    }}
                    className="px-4 py-2 border border-gray-300 rounded text-sm text-gray-600 hover:bg-gray-50"
                  >
                    New Question
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                💡 Press Enter to send, Shift+Enter for new line
              </p>
            </div>

            {/* Chat response */}
            {chatResponse && (
              <div className="bg-gray-50 rounded p-4 border border-gray-200">
                <p className="text-sm font-semibold mb-2 text-blue-700">🤖 AI Response:</p>
                <p className="text-sm whitespace-pre-wrap">{chatResponse}</p>
                <div className="flex gap-2 mt-3">
                  <button className="text-xs text-gray-600 hover:text-green-600 flex items-center">
                    <ThumbsUp className="w-3 h-3 mr-1" />
                    Helpful
                  </button>
                  <button className="text-xs text-gray-600 hover:text-red-600 flex items-center">
                    <ThumbsDown className="w-3 h-3 mr-1" />
                    Not helpful
                  </button>
                </div>
              </div>
            )}

            {/* Safety notice */}
            <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded p-3 text-xs">
              <p className="font-semibold text-yellow-800">⚠️ AI Safety Notice</p>
              <p className="text-yellow-700 mt-1">
                AI assists with data extraction and categorization. Final claim calculations are performed by our court-tested BI engine. Always review AI suggestions.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
